import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import axiosBase from 'axios';
import Cookie from 'js-cookie';

type TendersState = {
  data: CircaTypes.TendersMap | null;
  promise: 'idle' | 'pending' | 'fulfilled' | 'rejected';
  error: string | null;
  token: string | null;
};

export const axios = axiosBase.create({
  baseURL: process.env.REACT_APP_BUYERS_BE,
  withCredentials: true,
});

const CIRCA_TOKEN_COOKIE = `buyerCircaToken${
  process.env.REACT_APP_ENV === 'prod' ? '' : `_${process.env.REACT_APP_ENV}`
}`;

const initialState: TendersState = {
  data: null,
  promise: 'idle',
  error: null,
  token: null,
};

export const fetchTenders = createAsyncThunk('tenders/fetchTenders', async (_, thunkAPI) => {
  const token = Cookie.get(CIRCA_TOKEN_COOKIE) as string;
  if (!token && process.env.REACT_APP_ENV === 'prod') {
    window.rollbar?.error('Failed to get circa token');
  }
  try {
    const { data } = await axios.get<CircaTypes.TenderData>('/tenders');

    return { data, token };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: 'Failed to fetch tenders', token });
  }
});

const tendersSlice = createSlice({
  name: 'tenders',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchTenders.pending, (state) => {
      state.promise = 'pending';
      state.error = null;
    });
    builder.addCase(
      fetchTenders.fulfilled,
      (state, action: PayloadAction<{ data: CircaTypes.TenderData; token: string }>) => {
        const { diamonds, jewelry, watches } = action.payload.data;

        return {
          ...state,
          promise: 'fulfilled',
          error: null,
          token: action.payload.token,
          data: {
            diamonds:
              diamonds === null
                ? null
                : {
                    ...diamonds,
                    tenderId: Number(diamonds.tenderId),
                    url: `${process.env.REACT_APP_CIRCA_TENDERS_URL}/tenders/${diamonds.tenderId}`,
                  },
            jewelry:
              jewelry === null
                ? null
                : {
                    ...jewelry,
                    tenderId: Number(jewelry.tenderId),
                    url: `${process.env.REACT_APP_CIRCA_TENDERS_URL}/tenders/${jewelry.tenderId}`,
                  },
            watches:
              watches === null
                ? null
                : {
                    ...watches,
                    tenderId: Number(watches.tenderId),
                    url: `${process.env.REACT_APP_CIRCA_TENDERS_URL}/tenders/${watches.tenderId}`,
                  },
          },
        };
      },
    );
    builder.addCase(fetchTenders.rejected, (state, action) => {
      if (action.payload) {
        const payload = action.payload as { error: string; token: string };
        return {
          ...state,
          promise: 'rejected',
          token: payload.token,
          error: payload.error,
        };
      }
      return {
        ...state,
        promise: 'rejected',
      };
    });
  },
});

export const tendersActions = { ...tendersSlice.actions, fetchTenders };

export default tendersSlice.reducer;
